import React, { useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Button from "@react/shared/ui/Button"
import IconButton from "@react/shared/ui/IconButton"
import { QLINE_TYPE_META } from "../helpers/costCategories"
import { formatMoney } from "../helpers/poTotals"
import ProductSearch from "./ProductSearch"

const t = (key, opts) => I18n.t(`projects.engagements.form.${key}`, opts)

const GRID  = "grid grid-cols-[minmax(0,1fr)_150px_84px_64px_110px_120px_34px] gap-2 items-center"
const CELL  = "h-8 px-2 rounded-md border border-slate-200 text-[12.5px] focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-400"
const RIGHT = `${CELL} text-right tabular-nums`

export const emptyFreeLine = (key, costCategory = "materials") => ({
  key, description: "", cost_category: costCategory, quantity: 1, unit: "", unit_price: "", product_id: null,
})

const FreeLinesEditor = ({ lines, currency, costCategories, productSearchUrl, onAdd, onUpdate, onRemove }) => {
  const [picking, setPicking] = useState(false)
  const categories = (costCategories && costCategories.length ? costCategories : Object.keys(QLINE_TYPE_META))

  const addFromProduct = product => {
    onAdd({
      description:   product.designation || product.name,
      cost_category: categories.includes(product.cost_category) ? product.cost_category : categories[0],
      quantity:      1,
      unit:          product.unit || "",
      unit_price:    (product.purchase_price?.cents || 0) / 100,
      product_id:    product.id,
    })
    setPicking(false)
  }

  return (
    <section>
      <div className="flex items-start justify-between gap-4 mb-2">
        <div>
          <h3 className="text-[13px] font-semibold text-slate-800">{t("free_lines.title")}</h3>
          <p className="text-[11.5px] text-slate-500 mt-0.5">{t("free_lines.hint")}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {productSearchUrl && (
            <Button variant="secondary" size="xs" onClick={() => setPicking(value => !value)}>
              <FontAwesomeIcon icon="magnifying-glass" className="mr-1" />{t("free_lines.from_catalog")}
            </Button>
          )}
          <Button variant="secondary" size="xs" onClick={() => onAdd({})}>
            <FontAwesomeIcon icon="plus" className="mr-1" />{t("free_lines.add")}
          </Button>
        </div>
      </div>

      {picking && (
        <div className="mb-2 max-w-md">
          <ProductSearch url={productSearchUrl} placeholder={t("breakdowns.search_product")} onSelect={addFromProduct} autoFocus />
        </div>
      )}

      {lines.length === 0 ? (
        <div className="px-4 py-4 text-center text-[12.5px] text-slate-400 border border-dashed border-slate-200 rounded-xl">{t("free_lines.empty")}</div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className={`${GRID} px-3 py-2 bg-slate-50/70 border-b border-slate-200 text-[10.5px] uppercase tracking-wider text-slate-500 font-medium`}>
            <div>{t("designation")}</div>
            <div>{t("free_lines.cost_category")}</div>
            <div className="text-right">{t("quantity")}</div>
            <div>{t("unit")}</div>
            <div className="text-right">{t("unit_price")}</div>
            <div className="text-right">{t("line_total")}</div>
            <div />
          </div>
          <div className="divide-y divide-slate-100">
            {lines.map(line => {
              const total = (parseFloat(line.quantity) || 0) * (parseFloat(line.unit_price) || 0)
              return (
                <div key={line.key} className={`${GRID} px-3 py-2`}>
                  <input className={CELL} value={line.description} placeholder={t("line_description")} onChange={event => onUpdate(line.key, { description: event.target.value })} />
                  <select className={`${CELL} bg-white`} value={line.cost_category} onChange={event => onUpdate(line.key, { cost_category: event.target.value })}>
                    {categories.map(category => <option key={category} value={category}>{QLINE_TYPE_META[category]?.label || category}</option>)}
                  </select>
                  <input type="number" step="0.01" min="0" className={RIGHT} value={line.quantity} onChange={event => onUpdate(line.key, { quantity: event.target.value })} />
                  <input className={CELL} value={line.unit} placeholder="u" onChange={event => onUpdate(line.key, { unit: event.target.value })} />
                  <input type="number" step="0.01" min="0" className={RIGHT} value={line.unit_price} onChange={event => onUpdate(line.key, { unit_price: event.target.value })} />
                  <div className="text-right tabular-nums text-[12.5px] font-medium text-slate-900">{formatMoney(total, currency)}</div>
                  <IconButton tone="danger" size="sm" title={t("remove_line")} onClick={() => onRemove(line.key)}>
                    <FontAwesomeIcon icon="minus" />
                  </IconButton>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </section>
  )
}

export default FreeLinesEditor
